import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spin, Typography } from "antd";
import { DownloadOutlined, ExpandOutlined } from "@ant-design/icons";
import {
  createCommercialInvoiceSignedUrl,
  fetchCommercialInvoiceBlob,
} from "../../utils/commercialInvoiceUtils";
import { getFileType } from "../../utils/containerDocumentsUtils";
import {
  ExcelPreviewData,
  parseExcelBlobForPreview,
} from "../../utils/excelPreviewUtils";

const { Text } = Typography;

const PREVIEW_HEIGHT = 140;
const EXCEL_PREVIEW_MAX_ROWS = 50;

interface AgentCommercialInvoicePreviewProps {
  commercialInvoiceFile: string | null | undefined;
  bookingId: number;
  onDownload: () => void;
  downloading?: boolean;
}

const ExcelPreviewTable: React.FC<{
  data: ExcelPreviewData;
  height: number | string;
}> = ({ data, height }) => {
  const colCount = data.rows.reduce((max, row) => Math.max(max, row.length), 0);

  return (
    <div style={{ height, overflow: "auto", background: "#fff" }}>
      {data.truncated ? (
        <Text
          type="secondary"
          style={{ fontSize: 10, display: "block", padding: "4px 8px" }}
        >
          Showing first {EXCEL_PREVIEW_MAX_ROWS} rows
        </Text>
      ) : null}
      <table
        style={{
          borderCollapse: "collapse",
          fontSize: 10,
          width: "max-content",
          minWidth: "100%",
        }}
      >
        <tbody>
          {data.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: colCount }, (_, colIndex) => (
                <td
                  key={colIndex}
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "2px 6px",
                    whiteSpace: "nowrap",
                    maxWidth: 160,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: rowIndex === 0 ? "#fafafa" : undefined,
                    fontWeight: rowIndex === 0 ? 600 : undefined,
                  }}
                >
                  {row[colIndex] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AgentCommercialInvoicePreview: React.FC<
  AgentCommercialInvoicePreviewProps
> = ({ commercialInvoiceFile, bookingId, onDownload, downloading = false }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const invoicePaths = useMemo(
    () =>
      commercialInvoiceFile
        ?.split(",")
        .map((path) => path.trim())
        .filter(Boolean) ?? [],
    [commercialInvoiceFile]
  );

  const primaryPath = invoicePaths[0] ?? null;
  const primaryFileName = primaryPath?.split("/").pop() ?? "Commercial invoice";
  const fileType = getFileType(primaryFileName);
  const isExcel = fileType.type === "excel";
  const canInlinePreview =
    fileType.type === "pdf" || fileType.type === "image" || isExcel;
  const hasPreviewContent = isExcel ? !!excelPreview : !!previewUrl;

  useEffect(() => {
    if (!primaryPath) {
      setPreviewUrl(null);
      setExcelPreview(null);
      return;
    }

    let active = true;
    setPreviewLoading(true);
    setPreviewUrl(null);
    setExcelPreview(null);

    const loadPreview = isExcel
      ? fetchCommercialInvoiceBlob(primaryPath, bookingId).then((blob) =>
          parseExcelBlobForPreview(blob, EXCEL_PREVIEW_MAX_ROWS)
        )
      : createCommercialInvoiceSignedUrl(primaryPath, 3600, bookingId);

    loadPreview
      .then((result) => {
        if (!active) {
          if (typeof result === "string" && result) URL.revokeObjectURL(result);
          return;
        }
        if (isExcel) {
          setExcelPreview(result as ExcelPreviewData);
          return;
        }
        const url = result as string | null;
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (active) {
          setPreviewUrl(null);
          setExcelPreview(null);
        }
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });

    return () => {
      active = false;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [primaryPath, bookingId, isExcel]);

  const closeModal = () => setModalOpen(false);

  if (!primaryPath) return null;

  const previewContent = (height: number | string, interactive: boolean) => {
    if (previewLoading) {
      return (
        <div
          style={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="small" />
        </div>
      );
    }

    if (isExcel) {
      if (!excelPreview || excelPreview.rows.length === 0) {
        return (
          <div
            style={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
              {excelPreview?.rows.length === 0
                ? "Empty spreadsheet"
                : primaryFileName}
            </Text>
          </div>
        );
      }
      return <ExcelPreviewTable data={excelPreview} height={height} />;
    }

    if (!previewUrl || !canInlinePreview) {
      return (
        <div
          style={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
            {primaryFileName}
          </Text>
        </div>
      );
    }

    if (fileType.type === "pdf") {
      return (
        <iframe
          src={`${previewUrl}#toolbar=0&navpanes=0`}
          style={{
            width: "100%",
            height,
            border: "none",
            pointerEvents: interactive ? "auto" : "none",
          }}
          title={`Preview: ${primaryFileName}`}
        />
      );
    }

    return (
      <img
        src={previewUrl}
        alt={primaryFileName}
        style={{
          width: "100%",
          height,
          objectFit: "contain",
          display: "block",
          background: "#fff",
        }}
      />
    );
  };

  return (
    <>
      <div style={{ marginBottom: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {primaryFileName}
          {invoicePaths.length > 1
            ? ` (+${invoicePaths.length - 1} more)`
            : null}
          {isExcel && excelPreview?.sheetName
            ? ` · ${excelPreview.sheetName}`
            : null}
        </Text>
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => hasPreviewContent && setModalOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (hasPreviewContent) setModalOpen(true);
          }
        }}
        style={{
          height: PREVIEW_HEIGHT,
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          background: "#fafafa",
          cursor: hasPreviewContent ? "pointer" : "default",
          position: "relative",
        }}
      >
        {previewContent(PREVIEW_HEIGHT, false)}
        {hasPreviewContent && !previewLoading ? (
          <div
            style={{
              position: "absolute",
              right: 8,
              bottom: 8,
              background: "rgba(255,255,255,0.92)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 11,
              color: "#595959",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ExpandOutlined style={{ fontSize: 10 }} />
            Expand
          </div>
        ) : null}
      </div>

      <Modal
        title={primaryFileName}
        open={modalOpen}
        onCancel={closeModal}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={onDownload}
          >
            Download
          </Button>,
          <Button key="close" onClick={closeModal}>
            Close
          </Button>,
        ]}
        width="min(900px, calc(100vw - 32px))"
      >
        <div style={{ height: "70vh", overflow: "auto" }}>
          {previewContent("100%", true)}
        </div>
      </Modal>
    </>
  );
};
